import { useState, useEffect, useCallback, useRef } from "react";
import { ArrowLeft, Send, MessageCircle, User, Package, DollarSign } from "lucide-react";
import * as authClient from "@/app/api/auth-client";
import { apiMessageItemToChatMessage } from "@/app/api/auth-client";
import { Button } from "@/app/components/button";
import { Input } from "@/app/components/input";
import { BottomNav } from "@/app/components/bottom-nav";
import { formatCurrency } from "@/config/locale";

export interface OfferMessageData {
  offerId: string;
  offerType: "cash-only" | "product-plus-cash";
  totalOfferValue: number;
  targetAdTitle: string;
  status: "pending" | "accepted" | "rejected" | "countered";
}

interface Message {
  id: string;
  text: string;
  sender: "user" | "other";
  timestamp: Date;
  messageType?: string;
  offerData?: OfferMessageData;
}

/** Contextual label for offer-related messages (buyer vs seller). Aligns with event direction: sent = right, received = left. */
function getOfferMessageLabel(message: Message): string | null {
  const t = message.messageType;
  const isUser = message.sender === "user";
  if (t === "offer_accepted") return isUser ? "Você aceitou a oferta" : "O vendedor aceitou a oferta";
  if (t === "offer_rejected") return isUser ? "Você recusou a oferta" : "O vendedor recusou a oferta";
  if (t === "offer_withdrawn") return isUser ? "Você retirou a oferta" : "O comprador retirou a oferta";
  if (t === "offer_countered") return isUser ? "Contra-oferta enviada" : "Contra-oferta recebida";
  if (t === "offer_received" || (message.offerData && !t)) return isUser ? "Oferta enviada" : "Nova oferta recebida";
  return null;
}

/**
 * Which offers screen "Verificar Oferta" should open: App maps `user` → Ofertas Enviadas, `other` → Recebidas.
 * Chat bubble `sender` is who posted the system line — for contra-ofertas that is inverted vs listing role:
 * "Contra-oferta recebida" appears as `other` but the viewer is the comprador (oferta original) → Enviadas.
 */
function getOffersPageSenderForOfferCard(message: Message): "user" | "other" {
  if (message.messageType === "offer_countered") {
    return message.sender === "user" ? "other" : "user";
  }
  return message.sender;
}

export interface Conversation {
  id: string;
  name: string;
  type: "support" | "seller";
  adTitle?: string;
}

interface ChatPageProps {
  onBack: () => void;
  activeTab: "home" | "favorites";
  onTabChange: (tab: "home" | "favorites") => void;
  favoritesCount: number;
  onMenuClick: () => void;
  onPostAdClick: () => void;
  postAdBlockedByUnverifiedEmail?: boolean;
  conversationName: string;
  conversationType: "support" | "seller";
  adTitle?: string;
  /** Other party's profile image URL (from Bonamassa GET /users/:id). Shown in header when present. */
  otherPartyProfileImageUrl?: string;
  /** When set, messages are loaded from BFF and send goes via API. */
  conversationId?: string;
  /** Current user id (for mapping message sender). Required when conversationId is set. */
  userId?: string;
  initialMessages?: Message[];
  onSendMessage: (message: string) => void;
  onViewOfferDetails?: (offerId: string, sender: "user" | "other") => void;
  /** When true, do not render the bottom nav (e.g. when embedded in chat modal). */
  hideBottomNav?: boolean;
  /** When true, do not render the page header (e.g. when embedded in chat modal with its own header). */
  hidePageHeader?: boolean;
  /** Called after opening a thread with mark_read so parent can zero local unread (floating badge). */
  onThreadViewed?: (conversationId: string) => void;
}

export function ChatPage({
  onBack,
  activeTab,
  onTabChange,
  favoritesCount,
  onMenuClick,
  onPostAdClick,
  postAdBlockedByUnverifiedEmail = false,
  conversationName,
  conversationType,
  adTitle,
  otherPartyProfileImageUrl,
  conversationId,
  userId,
  initialMessages,
  onSendMessage,
  onViewOfferDetails,
  hideBottomNav,
  hidePageHeader,
  onThreadViewed,
}: ChatPageProps) {
  const [messages, setMessages] = useState<Message[]>(initialMessages || []);
  const [newMessage, setNewMessage] = useState("");
  const [messagesLoading, setMessagesLoading] = useState(!!conversationId);
  const [sendError, setSendError] = useState<Error | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const onThreadViewedRef = useRef(onThreadViewed);
  onThreadViewedRef.current = onThreadViewed;

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, []);

  const POLL_INTERVAL_MS = 5000;

  const loadMessages = useCallback((opts?: { silent?: boolean }) => {
    if (!conversationId || !userId) return;
    if (!opts?.silent) setMessagesLoading(true);
    authClient
      .getMessages(conversationId, {
        limit: 100,
        markRead: !opts?.silent,
      })
      .then((res) => {
        const list = (res.items ?? []).map((item) => {
          const m = apiMessageItemToChatMessage(item, userId);
          return { ...m, messageType: m.messageType } as Message;
        });
        setMessages(list);
        if (!opts?.silent) onThreadViewedRef.current?.(conversationId);
      })
      .catch(() => {
        if (!opts?.silent) setMessages([]);
      })
      .finally(() => {
        if (!opts?.silent) setMessagesLoading(false);
      });
  }, [conversationId, userId]);

  useEffect(() => {
    if (conversationId && userId) {
      loadMessages();
    } else if (initialMessages?.length) {
      setMessages(initialMessages);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only run when switching conversation
  }, [conversationId, userId]);

  useEffect(() => {
    if (!conversationId || !userId) return;
    const interval = setInterval(() => loadMessages({ silent: true }), POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [conversationId, userId, loadMessages]);

  useEffect(() => {
    if (!messagesLoading && messages.length > 0) {
      scrollToBottom();
    }
  }, [messagesLoading, messages.length, scrollToBottom]);

  const handleSendMessage = () => {
    const text = newMessage.trim();
    if (!text) return;

    if (conversationId && userId) {
      setSendError(null);
      const optimistic: Message = {
        id: `temp-${Date.now()}`,
        text,
        sender: "user",
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, optimistic]);
      onSendMessage(text);
      setNewMessage("");
      authClient
        .sendMessage(conversationId, text)
        .then((created) => {
          const mapped = apiMessageItemToChatMessage(created, userId) as Message;
          setMessages((prev) =>
            prev.map((m) => (m.id === optimistic.id ? mapped : m))
          );
        })
        .catch((err) => {
          setSendError(err);
          setMessages((prev) => prev.filter((m) => m.id !== optimistic.id));
        });
    } else {
      const userMessage: Message = {
        id: Date.now().toString(),
        text,
        sender: "user",
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, userMessage]);
      onSendMessage(text);
      setNewMessage("");
    }
  };

  const isLoading = messagesLoading;

  const formatTime = (date: Date) => {
    return date.toLocaleTimeString("pt-BR", {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <div className={`flex flex-col ${hideBottomNav ? "min-h-0 flex-1" : ""}`}>
      {!hidePageHeader && (
        <header className="sticky top-0 z-40 bg-card border-b border-border shadow-sm shrink-0">
          <div className="flex items-center justify-between p-4">
            <div className="flex items-center gap-3 flex-1">
              <button
                onClick={onBack}
                className="p-2 hover:bg-accent rounded-lg transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div className="flex items-center gap-3 flex-1 min-w-0">
                <div className="w-10 h-10 bg-primary rounded-full flex items-center justify-center shrink-0 overflow-hidden">
                  {conversationType === "support" ? (
                    <MessageCircle className="w-5 h-5 text-white" />
                  ) : otherPartyProfileImageUrl ? (
                    <img
                      src={otherPartyProfileImageUrl}
                      alt=""
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <User className="w-5 h-5 text-white" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <h2 className="text-base truncate">{conversationName}</h2>
                  {adTitle ? (
                    <p className="text-xs text-muted-foreground truncate">
                      Sobre: {adTitle}
                    </p>
                  ) : (
                    <p className="text-xs text-muted-foreground">Online</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </header>
      )}

      {/* Messages: min-h-0 allows flex child to shrink and scroll */}
      <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden p-4 pb-4 space-y-4 overscroll-contain">
        {isLoading ? (
          <div className="flex justify-center py-8">
            <p className="text-sm text-muted-foreground">Carregando mensagens...</p>
          </div>
        ) : sendError ? (
          <div className="rounded-lg bg-destructive/10 text-destructive text-sm p-3">
            Falha ao enviar. Tente novamente.
          </div>
        ) : null}
        {!isLoading && messages.map((message) => (
          <div
            key={message.id}
            className={`flex ${message.sender === "user" ? "justify-end" : "justify-start"}`}
          >
            <div className="max-w-[85%] space-y-2">
              {/* Offer Card (if message has offer data) */}
              {message.offerData && (
                <div className="bg-card border-2 border-primary/30 rounded-xl p-4 shadow-sm">
                  <div className="flex items-start gap-3 mb-3">
                    <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center shrink-0">
                      <Package className="w-5 h-5 text-primary" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="font-semibold text-sm mb-1">
                        {getOfferMessageLabel(message) ?? (message.sender === "other" ? "Oferta Recebida" : "Oferta Enviada")}
                      </h4>
                      <p className="text-xs text-muted-foreground line-clamp-1">
                        {message.offerData.targetAdTitle}
                      </p>
                    </div>
                  </div>
                  
                  <div className="bg-secondary/50 rounded-lg p-3 mb-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <DollarSign className="w-4 h-4 text-primary" />
                        <span className="text-xs font-medium">Valor da Oferta</span>
                      </div>
                      <span className="font-bold text-primary">
                        {formatCurrency(message.offerData.totalOfferValue)}
                      </span>
                    </div>
                    <div className="mt-2 text-xs text-muted-foreground">
                      Tipo: {message.offerData.offerType === "cash-only" ? "Apenas Dinheiro" : "Produto + Dinheiro"}
                    </div>
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <span className={`text-xs px-2 py-1 rounded-full ${
                      message.offerData.status === "pending" ? "bg-yellow-500/10 text-yellow-700 dark:text-yellow-400" :
                      message.offerData.status === "accepted" ? "bg-green-500/10 text-green-700 dark:text-green-400" :
                      message.offerData.status === "rejected" ? "bg-red-500/10 text-red-700 dark:text-red-400" :
                      "bg-blue-500/10 text-blue-700 dark:text-blue-400"
                    }`}>
                      {message.offerData.status === "pending" ? "Aguardando resposta" :
                       message.offerData.status === "accepted" ? "Aceita" :
                       message.offerData.status === "rejected" ? "Recusada" :
                       "Contra-proposta"}
                    </span>
                    <button
                      type="button"
                      onClick={() =>
                        onViewOfferDetails?.(
                          message.offerData!.offerId,
                          getOffersPageSenderForOfferCard(message)
                        )
                      }
                      className="text-xs text-primary font-medium hover:underline"
                    >
                      Verificar Oferta
                    </button>
                  </div>
                </div>
              )}
              
              {/* Text Message: show when there is text or a contextual offer label */}
              {(message.text || getOfferMessageLabel(message)) && (
                <div
                  className={`rounded-2xl px-4 py-3 ${
                    message.sender === "user"
                      ? "bg-primary text-primary-foreground"
                      : "bg-secondary text-secondary-foreground"
                  }`}
                >
                  <p className="text-sm">
                    {getOfferMessageLabel(message) ?? message.text}
                  </p>
                  <p
                    className={`text-xs mt-1 ${
                      message.sender === "user"
                        ? "text-primary-foreground/70"
                        : "text-muted-foreground"
                    }`}
                  >
                    {formatTime(message.timestamp)}
                  </p>
                </div>
              )}
            </div>
          </div>
        ))}
        <div ref={messagesEndRef} aria-hidden />
      </div>

      {/* Input: when embedded in modal (hideBottomNav), use sticky at bottom of flex; otherwise fixed above bottom nav */}
      <div
        className={
          hideBottomNav
            ? "shrink-0 bg-card border-t border-border p-4"
            : "fixed bottom-20 left-0 right-0 bg-card border-t border-border p-4"
        }
      >
        <div className="flex gap-2 max-w-7xl mx-auto">
          <Input
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            placeholder="Digite sua mensagem..."
            onKeyPress={(e) => {
              if (e.key === "Enter") {
                handleSendMessage();
              }
            }}
            className="flex-1"
          />
          <Button
            onClick={handleSendMessage}
            size="icon"
            className="shrink-0"
            disabled={!newMessage.trim()}
          >
            <Send className="w-5 h-5" />
          </Button>
        </div>
      </div>

      {!hideBottomNav && (
        <BottomNav
          activeTab={activeTab}
          onTabChange={(tab) => onTabChange(tab as "home" | "favorites")}
          favoritesCount={favoritesCount}
          onMenuClick={onMenuClick}
          onPostAdClick={onPostAdClick}
          postAdBlockedByUnverifiedEmail={postAdBlockedByUnverifiedEmail}
        />
      )}
    </div>
  );
}